// Printable Pest Control Service Report.
//
// This renders the company's real form — the one the crew fills in by hand —
// pre-filled from a work order and printed per job from the office. It replaces
// nothing: the Forms Library's preview draws the same generic six fields plus a
// run of blank boxes for all 37 of its entries, so no real layout existed.
//
// Why a self-contained HTML document rather than a React component:
// `printElement` copies an element's innerHTML into a blank window carrying its
// own small stylesheet, so Tailwind classes are dropped and a precise form
// collapses. Generating the whole document here means what prints is exactly
// what is written, with no dependency on which stylesheet the app happens to
// load.
//
// The identifier block is what makes scanning safe. A paper report carries no
// invoice number — the invoice does not exist yet — so importing a stack of
// scans would otherwise have to match on (customer, date, amount), which is
// ambiguous in this data: the jobs export holds 25 duplicated (customer, date)
// pairs and the app's work orders hold 159. Printing the work order id makes
// every sheet uniquely identifiable, so re-scanning the same stack cannot
// create a second invoice for the same visit.

// Code 39. Chosen because it encodes in ~40 lines with no dependency; the
// `qrcode` package is not installed and its registry fetch fails from here.
// Each character is 5 bars and 4 spaces, three of them wide.
const CODE39 = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', 'A': 'wnnnnwnnw', 'B': 'nnwnnwnnw',
  'C': 'wnwnnwnnn', 'D': 'nnnnwwnnw', 'E': 'wnnnwwnnn', 'F': 'nnwnwwnnn',
  'G': 'nnnnnwwnw', 'H': 'wnnnnwwnn', 'I': 'nnwnnwwnn', 'J': 'nnnnwwwnn',
  'K': 'wnnnnnnww', 'L': 'nnwnnnnww', 'M': 'wnwnnnnwn', 'N': 'nnnnwnnww',
  'O': 'wnnnwnnwn', 'P': 'nnwnwnnwn', 'Q': 'nnnnnnwww', 'R': 'wnnnnnwwn',
  'S': 'nnwnnnwwn', 'T': 'nnnnwnwwn', 'U': 'wwnnnnnnw', 'V': 'nwwnnnnnw',
  'W': 'wwwnnnnnn', 'X': 'nwnnwnnnw', 'Y': 'wwnnwnnnn', 'Z': 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '.': 'wwnnnnwnn', ' ': 'nwwnnnwnn', '*': 'nwnnwnwnn',
};

// An SVG barcode for `text`, or '' when a character cannot be encoded — the
// caller still prints the id as text, so an unencodable id degrades to
// human- and OCR-readable rather than to nothing.
export function code39Svg(text, { height = 38, narrow = 1.6 } = {}) {
  const body = String(text || '').toUpperCase();
  const framed = `*${body}*`;
  const bars = [];
  let x = 0;
  for (const ch of framed) {
    const pattern = CODE39[ch];
    if (!pattern) return '';
    for (let i = 0; i < pattern.length; i += 1) {
      const w = pattern[i] === 'w' ? narrow * 2.6 : narrow;
      if (i % 2 === 0) bars.push(`<rect x="${x.toFixed(2)}" y="0" width="${w.toFixed(2)}" height="${height}"/>`);
      x += w;
    }
    x += narrow; // inter-character gap
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${x.toFixed(2)}" height="${height}" viewBox="0 0 ${x.toFixed(2)} ${height}" fill="#000">${bars.join('')}</svg>`;
}

// A single check character over the id, so a misread digit is detectable rather
// than silently importing against the wrong job.
export function idCheckChar(id) {
  const s = String(id || '').toUpperCase();
  let sum = 0;
  for (let i = 0; i < s.length; i += 1) sum = (sum * 31 + s.charCodeAt(i)) % 36;
  return '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'[sum];
}

const esc = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// The 23 products the form prints, as a fallback when the catalog has not been
// seeded yet. The catalog is the source of truth: rows flagged onServiceReport
// are used when present, so adding a product there changes the printed form.
const FALLBACK_PRODUCTS = [
  ['Talstar P / Talstar One', 'Bifenthrin 7.9%', '279-3206'],
  ['Cy-Kick CS', 'Cyfluthrin 6.0%', '499-304'],
  ['Temprid FX', 'Imidacloprid 21% / b-Cyfluthrin 10.5%', '432-1544'],
  ['Suspend PolyZone', 'Deltamethrin 4.75%', '432-1514'],
  ['Demand CS', 'Lambda-cyhalothrin 9.7%', '100-1066'],
  ['Tandem', 'Thiamethoxam 11.6% / L-cyhal. 3.5%', '100-1437'],
  ['Phantom', 'Chlorfenapyr 21.45%', '241-392'],
  ['Termidor SC', 'Fipronil 9.1%', '7969-210'],
  ['Onslaught FastCap', 'Esfenvalerate 6.4% / Prallethrin 1.6%', '1021-2574'],
  ['Alpine WSG', 'Dinotefuran 40%', '499-561'],
  ['Talstar PL Granular', 'Bifenthrin 0.2%', '279-3168'],
  ['Precor 2000 Plus', '(S)-Methoprene 0.085% / Permethrin', '2724-490'],
  ['Tekko Pro IGR', 'Pyriproxyfen 1.3% / Novaluron 1.3%', '53883-335'],
  ['Altosid Pro-G', '(S)-Methoprene 1.5%', '2724-451'],
  ['Advion Cockroach Gel', 'Indoxacarb 0.6%', '100-1484'],
  ['Vendetta Cockroach Gel', 'Abamectin 0.05%', '1021-1828'],
  ['Advion Ant Gel', 'Indoxacarb 0.05%', '100-1498'],
  ['Niban Granular Bait', 'Orthoboric acid 5%', '64405-2'],
  ['EcoVia EC', 'Thyme oil 20% — FIFRA 25(b) exempt', 'EXEMPT'],
  ['Contrac CA Soft Bait', 'Bromethalin 0.01%', '12455-159'],
  ['Contrac CA Blox', 'Bromethalin 0.01%', '12455-151'],
  ['TakeDown II Soft Bait', 'Bromethalin 0.01%', '7173-310'],
  ['Liqua-Tox II', 'Diphacinone 0.106%', '12455-61'],
];

const SITE_CODES = [['Kitchen', 'K'], ['Utility room', 'UR'], ['All interior', 'I'],
  ['All exterior', 'E'], ['Attic', 'A'], ['Basement', 'B'], ['Bedrooms', 'BR'],
  ['Prep area', 'PA'], ['Other', 'O']];
const METHOD_CODES = [['All above, inside', '23'], ['All above, outside', '24'],
  ['Crack & crevice', '91'], ['Spot treatment', '92'], ['General surface', '93'],
  ['Void treatment', '94'], ['Exterior applic.', '96'], ['Bait placement', '97']];
const EQUIP_CODES = [['Comp. sprayer', 'CS'], ['Power sprayer', 'PS'], ['Aerosol can', 'AC'],
  ['Hand duster', 'HD'], ['Power duster', 'PD'], ['Fogger', 'F'], ['ULV machine', 'ULV'],
  ['Bait gun', 'BG']];
const SERVICE_TYPES = ['Regular', 'Start / Initial', 'Call-back', 'Follow-up', 'Odd job', 'Inspection'];
const EQUIPMENT_SERVICED = ['Bait stations', 'Fly lights', 'Multi-catch traps', 'Mosquito stations'];
const PAY_METHODS = ['Card', 'Zelle', 'Check', 'Cash', 'Net-30'];
const ACTIVITY = ['No activity reported', 'German roaches', 'American roaches',
  'Oriental roaches', 'Rats & mice', 'Ants', 'Spiders', 'Flies'];

/** Products to print: the catalog's flagged rows, else the built-in list. */
export function formProducts(catalog) {
  const flagged = (catalog || [])
    .filter((p) => p.onServiceReport)
    .sort((a, b) => (a.serviceReportOrder || 0) - (b.serviceReportOrder || 0));
  if (flagged.length) {
    return flagged.map((p) => [
      p.name,
      [p.activeIngredient, p.concentration].filter(Boolean).join(' '),
      p.epaRegNo || 'EXEMPT',
    ]);
  }
  return FALLBACK_PRODUCTS;
}

const checkbox = (label) =>
  `<span class="cb"><span class="box"></span>${esc(label)}</span>`;

const codeList = (title, pairs) =>
  `<div class="legend"><div class="lt">${title}</div>${pairs
    .map(([l, c]) => `<div class="lr"><span>${esc(l)}</span><b>${esc(c)}</b></div>`)
    .join('')}</div>`;

function productRows(products) {
  return products
    .map(
      ([name, ai, epa]) => `<tr>
      <td class="pn">${esc(name)}</td><td class="ai">${esc(ai)}</td><td class="ep">${esc(epa)}</td>
      <td></td><td></td><td></td><td></td><td></td></tr>`
    )
    .join('');
}

function productTable(products) {
  return `<table class="pest">
    <thead><tr>
      <th class="pn">PESTICIDE / PRODUCT</th><th class="ai">ACTIVE INGREDIENT</th>
      <th class="ep">EPA REG. #</th><th>SITE</th><th>METH</th><th>EQUIP</th>
      <th>AMT USED</th><th>DILUT. %</th>
    </tr></thead><tbody>${productRows(products)}</tbody></table>`;
}

/**
 * The complete printable document for one job.
 *
 * `job` supplies what the office already knows, so the technician writes only
 * what happened on site. Everything left blank on paper stays blank here.
 */
export function serviceReportHtml({ job = {}, company = {}, products, balance = null } = {}) {
  const list = formProducts(products);
  const id = job.id || '';
  const check = id ? idCheckChar(id) : '';
  const stamped = id ? `${id}-${check}` : '';
  const bar = stamped ? code39Svg(stamped) : '';
  const addr = job.address
    || [job.city, job.state, job.zip].filter(Boolean).join(', ');

  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>Service Report${stamped ? ` ${stamped}` : ''}</title>
<style>
  @page { size: letter portrait; margin: 8mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; font-size: 8.2px; }
  .sheet { width: 100%; }
  h1 { font-size: 15px; margin: 0; letter-spacing: .5px; }
  .hdr { display: flex; justify-content: space-between; align-items: flex-start;
         border-bottom: 2px solid #000; padding-bottom: 4px; margin-bottom: 5px; }
  .co { font-size: 7.6px; line-height: 1.35; }
  .co b { font-size: 9px; }
  .idbox { text-align: center; border: 1.5px solid #000; padding: 3px 6px; min-width: 150px; }
  .idbox .lab { font-size: 6.4px; font-weight: bold; letter-spacing: .4px; }
  .idbox .num { font-family: "Courier New", monospace; font-size: 11px; font-weight: bold;
                letter-spacing: .5px; margin-top: 1px; }
  .idbox svg { display: block; margin: 2px auto 0; }
  .sec { border: 1px solid #000; margin-bottom: 4px; }
  .sec > .st { background: #000; color: #fff; font-size: 7px; font-weight: bold;
               letter-spacing: .6px; padding: 2px 5px; }
  .sec > .sb { padding: 4px 5px; }
  .row { display: flex; gap: 10px; }
  .fld { flex: 1; margin-bottom: 3px; }
  .fld .l { font-size: 6.6px; font-weight: bold; letter-spacing: .3px; }
  .fld .u { border-bottom: 1px solid #000; height: 13px; }
  .cb { display: inline-block; margin-right: 9px; white-space: nowrap; font-size: 7.4px; }
  .cb .box { display: inline-block; width: 7px; height: 7px; border: 1px solid #000;
             margin-right: 3px; vertical-align: -1px; }
  table.pest { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.pest th, table.pest td { border: 1px solid #000; padding: 1px 2px; font-size: 6.6px; }
  table.pest th { background: #e8e8e8; font-size: 5.9px; letter-spacing: .2px; }
  table.pest td { height: 12px; }
  table.pest .pn { width: 21%; text-align: left; font-weight: bold; }
  table.pest .ai { width: 30%; text-align: left; }
  table.pest .ep { width: 13%; text-align: left; font-family: "Courier New", monospace; }
  .cols { display: flex; gap: 4px; }
  .cols > * { flex: 1; min-width: 0; }
  .bill td { font-size: 7.6px; padding: 1px 3px; }
  .bill .amt { border-bottom: 1px solid #000; width: 62px; }
  .notes { height: 46px; border-bottom: 1px solid #000; }
  .legend { border: 1px solid #000; padding: 2px 4px; }
  .legend .lt { font-weight: bold; font-size: 6.4px; border-bottom: 1px solid #000;
                margin-bottom: 1px; letter-spacing: .3px; }
  .legend .lr { display: flex; justify-content: space-between; font-size: 6.4px; line-height: 1.3; }
  .sig { border-bottom: 1px solid #000; height: 20px; }
  .foot { font-size: 6.4px; line-height: 1.35; border: 1px solid #000; padding: 3px 5px; }
  .foot b { text-transform: uppercase; }
  .scale span { display: inline-block; width: 15px; height: 13px; border: 1px solid #000;
                text-align: center; font-size: 6.6px; line-height: 13px; margin-right: 1px; }
</style></head><body><div class="sheet">

  <div class="hdr">
    <div>
      <h1>PEST CONTROL SERVICE REPORT</h1>
      <div class="co"><b>${esc(company.companyName || 'XpertPests Solutions')}</b><br/>
        ${esc(company.address || 'P.O. Box 4462, Carson, CA 90745')}<br/>
        ${esc(company.phone1 || '562-215-8002')} Booking &amp; Accounting &nbsp;·&nbsp;
        ${esc(company.phone2 || '310-629-9049')} Field Service<br/>
        ${esc(company.website || 'xpertpestssolutions.com')} &nbsp;·&nbsp; ${esc(company.email || '')}
      </div>
    </div>
    ${stamped ? `<div class="idbox">
      <div class="lab">WORK ORDER — DO NOT REMOVE</div>
      <div class="num">${esc(stamped)}</div>
      ${bar}
    </div>` : ''}
  </div>

  <div class="sec"><div class="st">CUSTOMER &amp; SERVICE</div><div class="sb">
    <div class="row">
      <div class="fld"><div class="l">NAME</div><div class="u">${esc(job.customer || '')}</div></div>
      <div class="fld"><div class="l">SERVICE DATE</div><div class="u">${esc(job.date || '')}</div></div>
    </div>
    <div class="row">
      <div class="fld" style="flex:2"><div class="l">SERVICE ADDRESS</div><div class="u">${esc(addr)}</div></div>
      <div class="fld"><div class="l">TIME IN / OUT</div><div class="u"></div></div>
    </div>
    <div class="row">
      <div class="fld"><div class="l">TECHNICIAN</div><div class="u">${esc(job.tech || '')}</div></div>
      <div class="fld"><div class="l">TECHNICIAN SIGNATURE</div><div class="u"></div></div>
    </div>
  </div></div>

  <div class="cols">
    <div class="sec" style="flex:1.45"><div class="st">TYPE OF SERVICE / EQUIPMENT</div><div class="sb">
      ${SERVICE_TYPES.map(checkbox).join('')}<br/>
      <div style="margin-top:3px">${EQUIPMENT_SERVICED.map(checkbox).join('')}</div>
    </div></div>
    <div class="sec" style="flex:1"><div class="st">BILLING</div><div class="sb">
      <table class="bill"><tbody>
        <tr><td>THIS SERVICE</td><td>$</td><td class="amt"></td></tr>
        <tr><td>PREVIOUS BALANCE</td><td>$</td><td class="amt">${balance == null ? '' : esc(Number(balance).toFixed(2))}</td></tr>
        <tr><td>TOTAL DUE</td><td>$</td><td class="amt"></td></tr>
        <tr><td>TOTAL PAID</td><td>$</td><td class="amt"></td></tr>
      </tbody></table>
      <div style="margin-top:2px">${PAY_METHODS.map(checkbox).join('')}</div>
    </div></div>
  </div>

  <div class="cols">
    <div>${productTable(list.slice(0, Math.ceil(list.length / 2)))}</div>
    <div>${productTable(list.slice(Math.ceil(list.length / 2)))}</div>
  </div>

  <div class="sec" style="margin-top:4px"><div class="st">TECHNICIAN NOTES — conditions found, sanitation, areas treated, recommendations, follow-up</div>
    <div class="sb"><div class="notes"></div></div></div>

  <div class="cols">
    <div class="sec"><div class="st">ACTIVITY FOUND</div><div class="sb">
      ${ACTIVITY.map(checkbox).join('')}${checkbox('Other:')}______
    </div></div>
    <div class="sec"><div class="st">CUSTOMER</div><div class="sb">
      <div style="font-size:6.8px;font-weight:bold">HOW SATISFIED ARE YOU? (1 = not satisfied, 10 = very happy)</div>
      <div class="scale" style="margin:2px 0">${[1,2,3,4,5,6,7,8,9,10].map((n) => `<span>${n}</span>`).join('')}</div>
      <div class="fld"><div class="l">CUSTOMER SIGNATURE</div><div class="sig"></div></div>
    </div></div>
  </div>

  <div class="cols">
    ${codeList('SITE TREATED', SITE_CODES)}
    ${codeList('METHOD USED', METHOD_CODES)}
    ${codeList('EQUIPMENT USED', EQUIP_CODES)}
  </div>

  <div class="foot" style="margin-top:4px">
    <b>Pesticides are poison.</b> Do not return to treated rooms for 2 hours minimum. Ventilate before re-entry.
    Do not tamper with rodenticide stations. &nbsp;Poison Control: 1-800-222-1222<br/>
    <b>Payment</b> due within 30 days; a $20.00 late fee applies after that. 2 or more skipped services =
    account dropped, new initial service required to resume. Card on file · check to
    ${esc(company.address || 'P.O. Box 4462, Carson, CA 90745')} · ${esc(company.phone1 || '562-215-8002')}<br/>
    <b>Refer a customer and get a free service</b> — call for details.
  </div>

</div></body></html>`;
}

/** Open the report in a print window. Returns false if the pop-up was blocked. */
export function printServiceReport(opts) {
  const w = window.open('', '_blank', 'width=900,height=1150');
  if (!w) return false;
  w.document.write(serviceReportHtml(opts));
  w.document.close();
  setTimeout(() => w.print(), 400);
  return true;
}
